import type { FormsApiClient } from '../api';
import type { LeadFormPublicConfig } from '../types';
import { placeholderFor } from './inContent';
import { renderLightbox } from './lightbox';
import { applyThemeVars, resolvePopupStyle, type LeadFormTheme, type RenderHandle } from './shared';

const STYLE = `
  :host { all: initial; }
  * { box-sizing: border-box; font-family: system-ui, -apple-system, sans-serif; }
  .step-one {
    border: none; cursor: pointer; padding: 12px 20px; border-radius: var(--cms-lf-button-radius, 8px);
    background: var(--cms-lf-button-bg); color: var(--cms-lf-button-text); font-size: 14px; font-weight: 600;
  }
  .step-one.floating {
    position: fixed; left: 50%; bottom: 24px; transform: translateX(-50%); z-index: 2147483000;
    box-shadow: 0 8px 24px rgba(0,0,0,0.25);
  }
`;

/** OptinMonster's "2-step"/MonsterLink pattern: a plain CTA — dropped into a
 * `[data-cms-lf="<slug>"]` placeholder if the site owner added one (so it can sit inline in
 * existing copy, e.g. "click here for 10% off"), otherwise a floating bottom-center button — that
 * opens the real form (a standard lightbox panel) on click, rather than showing the form itself
 * up front. */
export function renderTwoStep(
  form: LeadFormPublicConfig,
  client: FormsApiClient,
  onSubmit: (data: Record<string, unknown>) => Promise<boolean>,
): RenderHandle {
  const theme = form.theme as LeadFormTheme;
  const resolved = resolvePopupStyle(theme);
  const mountPoint = placeholderFor(form.slug);

  const host = document.createElement('div');
  (mountPoint ?? document.body).appendChild(host);
  const root = host.attachShadow({ mode: 'open' });
  const styleEl = document.createElement('style');
  styleEl.textContent = STYLE;
  root.appendChild(styleEl);

  const button = document.createElement('button');
  button.type = 'button';
  button.className = mountPoint ? 'step-one' : 'step-one floating';
  button.textContent = theme.stepOneLabel || theme.ctaLabel || 'Click here';
  applyThemeVars(button, resolved);
  button.addEventListener('click', () => {
    button.style.display = 'none';
    const lightbox = renderLightbox(form, client, async (data) => {
      const ok = await onSubmit(data);
      return ok;
    });
    const originalClose = lightbox.close;
    lightbox.close = () => {
      originalClose();
      button.style.display = '';
    };
  });

  root.appendChild(button);

  function close() {
    host.remove();
  }

  return { close };
}
